<template>
<div class="mb-home">
  <MyvpnbookHeroSection
    :label="homeHero.label"
    :title="homeHero.title"
    :description="homeHero.description"
    :cta-label="homeHero.ctaLabel"
    :cta-to="homeHero.ctaTo"
    :points="homeHero.points"
  />

  <section class="mb-section mb-section--intro">
    <div class="mb-wrap">
      <h2 class="mb-title mb-title--center">{{ everydayFeatures.title }}</h2>
      <p class="mb-lead mb-lead--center">{{ everydayFeatures.intro }}</p>
      <div class="mb-features">
        <article v-for="item in everydayFeatures.items" :key="item.title" class="mb-feature">
          <span class="mb-feature__icon" v-html="glyphs[item.icon]" />
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
        </article>
      </div>
    </div>
  </section>

  <section id="servers" class="mb-section mb-section--soft">
    <div class="mb-wrap">
      <h2 class="mb-title">{{ locationIntro.title }}</h2>
      <p class="mb-lead">{{ locationIntro.lead }}</p>
      <ul class="mb-points">
        <li v-for="point in locationIntro.points" :key="point">{{ point }}</li>
      </ul>
      <div class="mb-servers">
        <component
          :is="loc.path ? 'router-link' : 'div'"
          v-for="loc in serverLocations"
          :key="loc.code"
          :to="loc.path || undefined"
          class="mb-server"
          :class="{ 'is-link': loc.path }"
        >
          <span class="mb-flag" v-html="flags[loc.code]" />
          <span class="mb-server__name">{{ loc.name }}</span>
          <span class="mb-server__status"><i /> Available</span>
        </component>
      </div>
    </div>
  </section>

  <section id="devices" class="mb-section">
    <div class="mb-wrap">
      <h2 class="mb-title">{{ deviceIntro.title }}</h2>
      <p class="mb-lead">{{ deviceIntro.lead }}</p>
      <div class="mb-devices">
        <router-link v-for="d in deviceCards" :key="d.to" :to="d.to" class="mb-device">
          <span class="mb-device__icon" :class="`is-${d.icon}`" v-html="deviceIcons[d.icon]" />
          <span class="mb-device__label">{{ d.label }}</span>
          <span class="mb-device__desc">{{ d.desc }}</span>
        </router-link>
      </div>
    </div>
  </section>

  <section id="protocols" class="mb-section mb-section--soft">
    <div class="mb-wrap">
      <h2 class="mb-title">{{ protocolIntro.title }}</h2>
      <p class="mb-lead">{{ protocolIntro.lead }}</p>
      <div class="mb-protocols">
        <article v-for="p in protocols" :key="p.title" class="mb-protocol">
          <span class="mb-protocol__mark" :class="`is-${p.tone}`" v-html="glyphs.shield" />
          <div>
            <h3>{{ p.title }}</h3>
            <p>{{ p.desc }}</p>
          </div>
        </article>
      </div>
    </div>
  </section>

  <section class="mb-section">
    <div class="mb-wrap">
      <h2 class="mb-title mb-title--center">How MyVPNBook VPN Works</h2>
      <p class="mb-lead mb-lead--center">{{ howItWorksIntro }}</p>
      <div class="mb-steps">
        <article v-for="(s, index) in howItWorks" :key="s.title" class="mb-step">
          <div class="mb-step__bubble">
            <span>{{ s.step }}</span>
            <i v-html="glyphs.step" />
          </div>
          <h3>{{ s.title }}</h3>
          <p>{{ s.desc }}</p>
          <span v-if="index < howItWorks.length - 1" class="mb-step__arrow" aria-hidden="true">→</span>
        </article>
      </div>
    </div>
  </section>

  <section class="mb-section mb-section--soft">
    <div class="mb-wrap">
      <div class="mb-head mb-head--stack">
        <div class="mb-head__row">
          <h2 class="mb-title">{{ problemIntro.title }}</h2>
          <router-link class="mb-more" :to="problemIntro.moreTo">{{ problemIntro.moreLabel }} →</router-link>
        </div>
        <p class="mb-lead mb-lead--wide">{{ problemIntro.lead }}</p>
      </div>
      <div class="mb-problems">
        <router-link v-for="p in problemCards" :key="p.title" :to="p.to" class="mb-problem">
          <span v-html="glyphs[p.icon]" />
          <strong>{{ p.title }}</strong>
          <span class="mb-problem__desc">{{ p.desc }}</span>
        </router-link>
      </div>
    </div>
  </section>

  <section id="guides" class="mb-section">
    <div class="mb-wrap">
      <div class="mb-head mb-head--stack">
        <div class="mb-head__row">
          <h2 class="mb-title">{{ guideIntro.title }}</h2>
          <router-link class="mb-more" :to="guideIntro.moreTo">{{ guideIntro.moreLabel }} →</router-link>
        </div>
        <p class="mb-lead mb-lead--wide">{{ guideIntro.lead }}</p>
      </div>
      <div class="mb-guides">
        <router-link v-for="g in guidePreviews" :key="g.path" :to="g.path" class="mb-guide">
          <h3>{{ g.title }}</h3>
          <p>{{ g.desc }}</p>
          <time>{{ g.date }}</time>
        </router-link>
      </div>
    </div>
  </section>

  <section id="faq" class="mb-section mb-section--soft">
    <div class="mb-wrap">
      <div class="mb-head">
        <div>
          <h2 class="mb-title">Frequently Asked Questions</h2>
          <p class="mb-lead">{{ faqIntro }}</p>
        </div>
      </div>
      <div class="mb-faqs">
        <article v-for="(item, index) in homeFaqs" :key="item.q" class="mb-faq" :class="{ 'is-open': openFaq === index }">
          <button type="button" class="mb-faq__q" :aria-expanded="openFaq === index ? 'true' : 'false'" @click="toggleFaq(index)">
            <span>{{ item.q }}</span>
            <i aria-hidden="true">{{ openFaq === index ? '−' : '+' }}</i>
          </button>
          <p v-if="openFaq === index" class="mb-faq__a">{{ item.a }}</p>
        </article>
      </div>
    </div>
  </section>
</div>
</template>

<script>
import MyvpnbookHeroSection from '@/components/myvpnbook/MyvpnbookHeroSection.vue'
import {
  homeHero,
  everydayFeatures,
  locationIntro,
  serverLocations,
  deviceIntro,
  deviceCards,
  protocolIntro,
  protocols,
  howItWorksIntro,
  howItWorks,
  problemIntro,
  problemCards,
  guideIntro,
  guidePreviews,
  faqIntro,
  homeFaqs,
} from '@/data/myvpnbookHome'

const icon = (paths) => `<svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">${paths}</svg>`
const featureIcon = (paths) => `<svg viewBox="0 0 24 24" width="32" height="32" fill="none" aria-hidden="true">${paths}</svg>`

export default {
  name: 'MyvpnbookHomeSections',
  components: { MyvpnbookHeroSection },
  data() {
    return {
      homeHero,
      everydayFeatures,
      locationIntro,
      serverLocations,
      deviceIntro,
      deviceCards,
      protocolIntro,
      protocols,
      howItWorksIntro,
      howItWorks,
      problemIntro,
      problemCards,
      guideIntro,
      guidePreviews,
      faqIntro,
      homeFaqs,
      openFaq: null,
      glyphs: {
        shield: icon('<path d="M12 3l7 2.6v5.2c0 4.4-2.9 8.2-7 9.7-4.1-1.5-7-5.3-7-9.7V5.6L12 3z" stroke="currentColor" stroke-width="1.7"/><path d="M9.2 12.1l1.8 1.8 3.8-4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>'),
        lock: icon('<rect x="6" y="10" width="12" height="9" rx="2" stroke="currentColor" stroke-width="1.7"/><path d="M8.5 10V8a3.5 3.5 0 017 0v2" stroke="currentColor" stroke-width="1.7"/>'),
        devices: icon('<rect x="3" y="5" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.7"/><rect x="9" y="10" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.7"/>'),
        globe: icon('<circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.7"/><path d="M4 12h16M12 4c2.2 2.4 3.3 5.1 3.3 8S14.2 17.6 12 20c-2.2-2.4-3.3-5.1-3.3-8S9.8 6.4 12 4z" stroke="currentColor" stroke-width="1.7"/>'),
        eye: icon('<path d="M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12z" stroke="currentColor" stroke-width="1.7"/><circle cx="12" cy="12" r="2.4" stroke="currentColor" stroke-width="1.7"/>'),
        wifi: icon('<path d="M5 10.5a9 9 0 0114 0M7.5 13a5.5 5.5 0 019 0" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="12" cy="16.5" r="1.3" fill="currentColor"/>'),
        'wifi-off': icon('<path d="M5 10.5a9 9 0 0114 0M8 13.2a5 5 0 016.2-.4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M5 5l14 14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>'),
        slow: icon('<circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.7"/><path d="M12 8v4.5l3 2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>'),
        refresh: icon('<path d="M19 12a7 7 0 11-2-4.9" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M19 5v4h-4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>'),
        alert: icon('<circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.7"/><path d="M12 8v5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="12" cy="16" r="1" fill="currentColor"/>'),
        step: icon('<circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.6"/>'),
        'ip-mask': featureIcon(
          '<rect x="3" y="6" width="18" height="12" rx="1.5" fill="#e8f1ff" stroke="#1b6fe8" stroke-width="1.3"/><path d="M6.5 10h7M6.5 13h5" stroke="#64748b" stroke-width="1.2" stroke-linecap="round"/><path d="M15.5 9.5l3.5 3.5-3.5 3.5" stroke="#1b6fe8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.5 8.5l3 3-3 3" stroke="#ef4444" stroke-width="1.2" stroke-linecap="round"/>'
        ),
        'tunnel-lock': featureIcon(
          '<path d="M5 12h14v8H5z" fill="#e8f1ff" stroke="#1b6fe8" stroke-width="1.3"/><path d="M8.5 12V9a3.5 3.5 0 017 0v3" stroke="#1b6fe8" stroke-width="1.5"/><rect x="10.5" y="14.5" width="3" height="3.5" rx=".6" fill="#1b6fe8"/><path d="M3 13.5c0-3 2.2-5.5 5-5.5M21 13.5c0-3-2.2-5.5-5-5.5" stroke="#93c5fd" stroke-width="1.3" stroke-linecap="round"/>'
        ),
        'wifi-shield': featureIcon(
          '<path d="M4.5 10.5a11 11 0 0115 0M7.5 13.5a7 7 0 019 0M10.5 16.5a3 3 0 013 0" stroke="#1b6fe8" stroke-width="1.4" stroke-linecap="round"/><circle cx="12" cy="18.5" r="1.2" fill="#1b6fe8"/><path d="M6 21.5h12l-2.5-4H8.5z" fill="#e8f1ff" stroke="#1b6fe8" stroke-width="1.2"/><path d="M12 4l5 2v3.5c0 3.2-2 5.2-5 6-3-0.8-5-2.8-5-6V6l5-2z" fill="#fff" fill-opacity=".85" stroke="#22c55e" stroke-width="1.1"/>'
        ),
        'globe-pin': featureIcon(
          '<circle cx="12" cy="11" r="7" fill="#e8f1ff" stroke="#1b6fe8" stroke-width="1.3"/><path d="M5 11h14M12 4c2 2.2 3 4.5 3 7s-1 4.8-3 7c-2-2.2-3-4.5-3-7s1-4.8 3-7z" stroke="#1b6fe8" stroke-width="1.1"/><path d="M12 18v3.5" stroke="#1b6fe8" stroke-width="1.5" stroke-linecap="round"/><circle cx="12" cy="22.5" r="1.8" fill="#1b6fe8"/>'
        ),
        'data-shield': featureIcon(
          '<path d="M12 3.5l7 2.8v5.2c0 4.6-3 7.4-7 8.8-4-1.4-7-4.2-7-8.8V6.3l7-2.8z" fill="#e8f1ff" stroke="#1b6fe8" stroke-width="1.3"/><path d="M9 12.5l2 2 4-4.5" stroke="#1b6fe8" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><rect x="16" y="5" width="5.5" height="7" rx=".8" fill="#fff" stroke="#64748b" stroke-width="1"/><path d="M17.2 7.2h3.2M17.2 9h2.2" stroke="#64748b" stroke-width=".9" stroke-linecap="round"/>'
        ),
      },
      deviceIcons: {
        android: icon('<path fill="#3ddc84" d="M7 9h10v8a2 2 0 01-2 2h-6a2 2 0 01-2-2V9zm1.2-2.2l-1-1.6 1.1-.7 1 1.5a5 5 0 014.4 0l1-1.5 1.1.7-1 1.6A4.8 4.8 0 0117 9H7a4.8 4.8 0 011.2-2.2zM9.2 7.2h.6V8h-.6V7.2zm5 0h.6V8h-.6V7.2z"/>'),
        windows: icon('<path fill="#0078d4" d="M4 6.2l6.2-.9v6.2H4V6.2zm7.2-1L20 4v7.5h-8.8V5.2zM4 12.5h6.2V19L4 18.1v-5.6zm7.2 0H20V20l-8.8-1.3v-6.2z"/>'),
        apple: icon('<path fill="#111" d="M15.2 8.2c.8-1 1.4-2.4 1.2-3.8-1.2.1-2.6.8-3.4 1.8-.7.9-1.4 2.3-1.2 3.6 1.3.1 2.6-.7 3.4-1.6zM16.6 9.4c-1.8-.1-3.3 1-4.2 1-.9 0-2.2-1-3.6-1-1.8 0-3.6 1.1-4.5 2.8-2 3.4-.5 8.4 1.4 11.2.9 1.3 2 2.8 3.4 2.7 1.3-.1 1.8-.9 3.4-.9s2 .9 3.5.8c1.5 0 2.4-1.3 3.3-2.6.9-1.5 1.3-2.9 1.3-3 0 0-2.6-1-2.6-3.9 0-2.4 2-3.6 2.1-3.7-1.2-1.7-3-1.9-3.6-1.9z"/>'),
        mac: icon('<path fill="#4b5568" d="M5 6h14a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1zm3 12h8v1.2H8V18z"/>'),
        chrome: icon('<circle cx="12" cy="12" r="8" fill="#fff" stroke="#4285f4" stroke-width="1.4"/><circle cx="12" cy="12" r="3" fill="#4285f4"/><path d="M12 9h7.2" stroke="#ea4335" stroke-width="2"/><path d="M8.2 15.2L12 9" stroke="#fbbc05" stroke-width="2"/><path d="M15.8 15.2H8.2" stroke="#34a853" stroke-width="2"/>'),
      },
      flags: {
        us: '<svg viewBox="0 0 60 40"><rect width="60" height="40" fill="#fff"/><g fill="#b22234"><rect width="60" height="3.08"/><rect y="6.15" width="60" height="3.08"/><rect y="12.3" width="60" height="3.08"/><rect y="18.46" width="60" height="3.08"/><rect y="24.6" width="60" height="3.08"/><rect y="30.77" width="60" height="3.08"/><rect y="36.92" width="60" height="3.08"/></g><rect width="26" height="21.5" fill="#3c3b6e"/></svg>',
        ru: '<svg viewBox="0 0 60 40"><rect width="60" height="13.4" fill="#fff"/><rect y="13.3" width="60" height="13.4" fill="#0039a6"/><rect y="26.6" width="60" height="13.4" fill="#d52b1e"/></svg>',
        kr: '<svg viewBox="0 0 60 40"><rect width="60" height="40" fill="#fff"/><circle cx="30" cy="20" r="8" fill="#cd2e3a"/><path d="M30 12a8 8 0 010 16 4 4 0 000-8 4 4 0 010-8z" fill="#0047a0"/></svg>',
        in: '<svg viewBox="0 0 60 40"><rect width="60" height="13.4" fill="#ff9933"/><rect y="13.3" width="60" height="13.4" fill="#fff"/><rect y="26.6" width="60" height="13.4" fill="#138808"/><circle cx="30" cy="20" r="4" fill="none" stroke="#000080" stroke-width="1"/></svg>',
        jp: '<svg viewBox="0 0 60 40"><rect width="60" height="40" fill="#fff"/><circle cx="30" cy="20" r="8" fill="#bc002d"/></svg>',
        mx: '<svg viewBox="0 0 60 40"><rect width="20" height="40" fill="#006847"/><rect x="20" width="20" height="40" fill="#fff"/><rect x="40" width="20" height="40" fill="#ce1126"/></svg>',
      },
    }
  },
  methods: {
    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? null : index
    },
  },
}
</script>

<style lang="scss" scoped>
.mb-home {
  background: #fff;
  color: var(--vpn-ink);
}

.mb-wrap {
  width: var(--vpn-content-width);
  max-width: 100%;
  margin: 0 auto;
}

.mb-section {
  padding: 64px 0;
}

.mb-section--soft {
  background: #f7faff;
}

.mb-title {
  margin: 0;
  font-size: clamp(26px, 3vw, 34px);
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.mb-title--center,
.mb-lead--center {
  text-align: center;
}

.mb-lead {
  margin: 10px 0 0;
  max-width: 640px;
  color: var(--vpn-ink-muted);
  font-size: 16px;
}

.mb-lead--center {
  margin-left: auto;
  margin-right: auto;
}

.mb-lead--wide {
  max-width: none;
  width: 100%;
}

.mb-section--intro .mb-lead--center {
  max-width: 780px;
}

.mb-section--intro .mb-feature {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.mb-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
}

.mb-head--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.mb-head__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.mb-head__row .mb-title {
  flex: 1;
  min-width: 0;
  margin: 0;
}

.mb-more {
  flex: none;
  border: 0;
  background: none;
  color: var(--vpn-brand);
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}

.mb-points {
  display: grid;
  gap: 8px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

.mb-points li {
  color: var(--vpn-ink);
  font-size: 15px;
}

.mb-points li::before {
  content: '✓';
  margin-right: 8px;
  color: var(--vpn-brand);
  font-weight: 700;
}

.mb-features,
.mb-devices,
.mb-problems {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;
  margin-top: 36px;
}

.mb-guides {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-top: 28px;
}

.mb-feature h3,
.mb-protocol h3,
.mb-step h3,
.mb-guide h3 {
  margin: 12px 0 6px;
  font-size: 16px;
  line-height: 1.35;
}

.mb-feature p,
.mb-protocol p,
.mb-step p,
.mb-guide time,
.mb-faq__a {
  margin: 0;
  color: var(--vpn-ink-muted);
  font-size: 14px;
  line-height: 1.55;
}

.mb-feature__icon,
.mb-problem span,
.mb-device__icon {
  color: var(--vpn-brand);
}

.mb-feature__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #f0f7ff;
}

.mb-feature__icon ::v-deep svg {
  width: 32px;
  height: 32px;
}

.mb-servers {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  margin-top: 28px;
}

.mb-server,
.mb-device,
.mb-protocol,
.mb-problem,
.mb-guide,
.mb-faq {
  background: #fff;
  border: 1px solid #e6eef8;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(27, 111, 232, 0.04);
}

.mb-server {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 118px;
  padding: 14px 12px;
  color: inherit;
  text-align: center;
  text-decoration: none;
}

.mb-server.is-link:hover,
.mb-device:hover,
.mb-problem:hover,
.mb-guide:hover {
  border-color: #b9d4fb;
  transform: translateY(-2px);
}

.mb-flag {
  display: block;
  width: 36px;
  height: 24px;
  overflow: hidden;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.08);
}

.mb-flag ::v-deep svg {
  display: block;
  width: 100%;
  height: 100%;
}

.mb-server__name,
.mb-device,
.mb-problem strong {
  font-size: 14px;
  font-weight: 700;
}

.mb-server__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #16a34a;
  font-size: 12px;
  font-weight: 700;
}

.mb-server__status i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
}

.mb-device {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-height: 168px;
  padding: 20px 16px;
  color: var(--vpn-ink);
  text-align: center;
  text-decoration: none;
}

.mb-device__label {
  width: 100%;
  font-size: 15px;
  font-weight: 700;
}

.mb-device__desc,
.mb-problem__desc {
  color: var(--vpn-ink-muted);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}

.mb-device__desc {
  width: 100%;
}

.mb-device__icon ::v-deep svg {
  width: 42px;
  height: 42px;
}

.mb-protocols {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-top: 28px;
}

.mb-protocol {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 22px 20px;
}

.mb-protocol__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  color: #fff;
  flex: none;
}

.mb-protocol__mark.is-red { background: #ef4444; }
.mb-protocol__mark.is-orange { background: #f59e0b; }
.mb-protocol__mark.is-blue { background: #3b82f6; }

.mb-protocol__mark ::v-deep svg {
  width: 22px;
  height: 22px;
}

.mb-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  margin-top: 36px;
  text-align: center;
}

.mb-step {
  position: relative;
  padding: 0 18px;
}

.mb-step__bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #e8f1ff;
  color: var(--vpn-brand);
  font-weight: 800;
}

.mb-step__bubble i {
  display: none;
}

.mb-step__arrow {
  position: absolute;
  top: 18px;
  right: -8px;
  color: #93c5fd;
  font-size: 22px;
}

.mb-problem {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  min-height: 112px;
  padding: 18px 16px;
  background: #f3f8ff;
  color: var(--vpn-ink);
  text-decoration: none;
}

.mb-guide {
  overflow: hidden;
  color: inherit;
  text-decoration: none;
}

.mb-guide__art {
  position: relative;
  display: block;
  height: 132px;
  overflow: hidden;
  background: #dbeafe center / cover no-repeat;
}

.mb-guide__art::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
}

.mb-guide__art.is-android {
  background: linear-gradient(160deg, #1d4ed8, #60a5fa 55%, #dbeafe);
}

.mb-guide__art.is-android::after,
.mb-guide__art.is-slow::after {
  width: 42px;
  height: 74px;
  border: 3px solid #fff;
  border-radius: 8px;
  background: #0f172a;
}

.mb-guide__art.is-windows {
  background: linear-gradient(160deg, #0f172a, #1e3a8a 40%, #38bdf8);
}

.mb-guide__art.is-windows::after {
  width: 86px;
  height: 54px;
  border: 3px solid #fff;
  border-radius: 6px;
  background: #dbeafe;
}

.mb-guide__art.is-server {
  background: linear-gradient(160deg, #1e40af, #38bdf8 60%, #e0f2fe);
}

.mb-guide__art.is-server::after {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #fff, #93c5fd 42%, #1d4ed8 43%);
}

.mb-guide__art.is-chrome {
  background: linear-gradient(160deg, #1d4ed8 0%, #facc15 45%, #22c55e 70%, #ef4444 100%);
}

.mb-guide__art.is-chrome::after {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: conic-gradient(#ea4335, #fbbc05, #34a853, #4285f4, #ea4335);
}

.mb-guide__art.is-slow {
  background: linear-gradient(160deg, #0f172a, #334155 50%, #94a3b8);
}

.mb-guide h3,
.mb-guide p,
.mb-guide time {
  display: block;
  padding: 0 14px;
}

.mb-guide h3 {
  margin-top: 14px;
}

.mb-guide time {
  padding-bottom: 16px;
}

.mb-faqs {
  display: grid;
  gap: 12px;
}

.mb-faq {
  overflow: hidden;
}

.mb-faq__q {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 16px 18px;
  border: 0;
  background: transparent;
  color: var(--vpn-ink);
  font: inherit;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.mb-faq__q i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #dbe4f0;
  border-radius: 50%;
  color: var(--vpn-ink-muted);
  font-style: normal;
}

.mb-faq__a {
  padding: 0 18px 16px;
}

@media (max-width: 1100px) {
  .mb-features,
  .mb-devices,
  .mb-problems {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .mb-servers,
  .mb-guides {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 800px) {
  .mb-protocols,
  .mb-steps {
    grid-template-columns: 1fr;
  }

  .mb-head:not(.mb-head--stack) {
    align-items: flex-start;
    flex-direction: column;
  }

  .mb-step__arrow {
    display: none;
  }

  .mb-features,
  .mb-devices,
  .mb-problems,
  .mb-guides,
  .mb-servers {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

}

@media (max-width: 520px) {
  .mb-features,
  .mb-problems,
  .mb-guides {
    grid-template-columns: 1fr;
  }
}
</style>
